

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { blogsAPI } from '../../lib/api';

export default function BlogDetailPage() {
  const params = useParams();
  const blogId = params.id;

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlog();
  }, [blogId]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      setError(null);
      // Note: The API doesn't have a getById endpoint for blogs
      // So we fetch all and filter, or you'll need to add this endpoint
      const blogs = await blogsAPI.getAll();
      const foundBlog = Array.isArray(blogs)
        ? blogs.find(b => b.id === parseInt(blogId))
        : null;

      if (foundBlog) {
        setBlog(foundBlog);
      } else {
        setError('Blog post not found');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching blog:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getReadingTime = (content) => {
    if (!content) return '1 min read';
    const words = content.trim().split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min read`;
  };

  if (loading) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded w-1/4 mb-8"></div>
              <div className="h-12 bg-gray-300 rounded w-3/4 mb-4"></div>
              <div className="h-6 bg-gray-300 rounded w-1/2 mb-8"></div>
              <div className="h-64 bg-gray-300 rounded-xl mb-8"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (error || !blog) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <svg
              className="w-24 h-24 text-gray-400 mx-auto mb-6"
             
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Blog Post Not Found</h2>
            <p className="text-gray-600 mb-8">{error || 'The blog post you are looking for does not exist.'}</p>
            <Link
              to="/blog"
              className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
            >
              <svg
                className="w-5 h-5 mr-2"
               
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Blog
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="relative min-h-screen bg-gray-50 overflow-x-hidden">
        {/* Animated Clouds */}
        <div className="cloud cloud1" />
        <div className="cloud cloud2" />
        <div className="cloud cloud3" />
        <div className="cloud cloud4" />

        {/* Airplane */}
        <img
          src="/airplane-svgrepo-com.svg"
          alt="Airplane"
          className="airplane"
        />

        <div className="relative z-20">
          {/* Breadcrumb */}
          <div className="bg-white border-b inset-x-0">
            <div className="max-w-4xl px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center text-sm text-gray-600">
                <Link to="/" className="hover:text-blue-600">Home</Link>
                <svg className="w-4 h-4 mx-2">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <Link to="/blog" className="hover:text-blue-600">Blog</Link>
                <svg className="w-4 h-4 mx-2">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-gray-900 font-medium truncate">{blog.title}</span>
              </div>
            </div>
          </div>

          {/* Article */}
          <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Article Header */}
            <header className="mb-8">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                {blog.title}
              </h1>

              {/* Meta Information */}
              <div className="flex flex-wrap items-center gap-4 text-gray-600">
                {blog.author && (
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold mr-3">
                      {blog.author.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{blog.author}</div>
                      {blog.publishedAt && (
                        <div className="text-sm text-gray-500">{formatDate(blog.publishedAt)}</div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-center text-gray-500">
                  <svg
                    className="w-5 h-5 mr-2"
                    stroke="currentColor" fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>{getReadingTime(blog.content)}</span>
                </div>
              </div>
            </header>

            {/* Featured Image */}
            <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
              <div className="relative h-64 sm:h-96 bg-gradient-to-br from-purple-400 via-pink-500 to-orange-500">
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg
                    className="w-24 h-24 text-white opacity-30"
                   
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Article Content */}
            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 lg:p-12">
              <div className="prose prose-lg max-w-none">
                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {blog.content || 'No content available for this blog post.'}
                </div>
              </div>

              {/* Share Section */}
              <div className="mt-12 pt-8 border-t">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Share this article</h3>
                <div className="flex gap-3">
                  <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center">
                    <svg className="w-5 h-5 mr-2">
                      <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
                    </svg>
                    Facebook
                  </button>
                  <button className="px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg transition-colors flex items-center">
                    <svg className="w-5 h-5 mr-2">
                      <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
                    </svg>
                    Twitter
                  </button>
                  <button className="px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-lg transition-colors flex items-center">
                    <svg className="w-5 h-5 mr-2">
                      <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
                    </svg>
                    Copy Link
                  </button>
                </div>
              </div>
            </div>

            {/* Back to Blog */}
            <div className="mt-8 text-center">
              <Link
                to="/blog"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold"
              >
                <svg
                  className="w-5 h-5 mr-2"
                 
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Back to All Articles
              </Link>
            </div>
          </article>

          {/* Newsletter CTA */}
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-8 sm:p-12 text-center text-white">
              <h3 className="text-2xl sm:text-3xl font-bold mb-4">
                Enjoy This Article?
              </h3>
              <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                Subscribe to our newsletter for more inspiring travel stories and exclusive deals.
              </p>
              <button className="px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-50 hover:bg-gradient-to-r hover:from-yellow-400 hover:to-orange-500 transition-all duration-200 hover:text-white shadow-lg">
                Subscribe Now
              </button>
            </div>
          </div>
        </div>
        <style jsx>{`
        .cloud {
          position: absolute;
          background: black;
          border-radius: 50%;
          filter: blur(60px);
          pointer-events: none;
        }

        .cloud1 {
          width: 250px;
          height: 120px;
          top: 10%;
          left: -100px;
          opacity: 0.9;
          animation: drift1 25s ease-in-out infinite;
        }

        .cloud2 {
          width: 350px;
          height: 150px;
          top: 30%;
          right: -150px;
          opacity: 0.8;
          animation: drift2 30s ease-in-out infinite 3s;
        }

        .cloud3 {
          width: 300px;
          height: 130px;
          bottom: 20%;
          left: 20%;
          opacity: 0.7;
          animation: drift3 35s ease-in-out infinite 7s;
        }

        .cloud4 {
          width: 280px;
          height: 120px;
          top: 50%;
          right: 30%;
          opacity: 0.9;
          animation: drift4 28s ease-in-out infinite 5s;
        }

        .airplane {
          position: absolute;
          opacity: 0.2;
          width: 80px;
          height: auto;
          top: 40%;
          left: -100px;
          z-index: 15;
          animation: flyAcross 15s linear infinite 4s;
        }

        @keyframes flyAcross {
          0% {
            left: -200px;
            transform: translateY(0);
          }
          50% {
            transform: translateY(-150px);
            rotate: 0deg;
          }
          75% {
            transform: translateY(-200px);
            rotate: 35deg;
          }
          100% {
            left: calc(100% + 300px);
            transform: translateY(0);
          }
        }

        @keyframes drift1 {
          0%, 100% {
            transform: translateX(0) translateY(0);
          }
          25% {
            transform: translateX(500px) translateY(-20px);
          }
          50% {
            transform: translateX(300px) translateY(20px);
          }
          75% {
            transform: translateX(-300px) translateY(-10px);
          }
        }

        @keyframes drift2 {
          0%, 100% {
            transform: translateX(0) translateY(0);
          }
          25% {
            transform: translateX(-500px) translateY(15px);
          }
          50% {
            transform: translateX(-400px) translateY(-25px);
          }
          75% {
            transform: translateX(400px) translateY(10px);
          }
        }

        @keyframes drift3 {
          0%, 100% {
            transform: translateX(0) translateY(0);
          }
          25% {
            transform: translateX(400px) translateY(25px);
          }
          50% {
            transform: translateX(-200px) translateY(-15px);
          }
          75% {
            transform: translateX(500px) translateY(20px);
          }
        }

        @keyframes drift4 {
          0%, 100% {
            transform: translateX(0) translateY(0);
          }
          25% {
            transform: translateX(-450px) translateY(-15px);
          }
          50% {
            transform: translateX(305px) translateY(30px);
          }
          75% {
            transform: translateX(-205px) translateY(15px);
          }
        }
      `}</style>
      </main>
      <Footer />
    </>
  );
}